import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Users, X } from "lucide-react";
type Role = "shipper" | "carrier" | "broker";

const TIER_CONFIG = {
  shipper: [
    {
      id: "free",
      name: "Free",
      price: null,
      billing: null,
      badge: null,
      description: "Get started at no cost",
      features: [
        "Post once — your partners are notified automatically",
        "3 shipments per month",
        "Receive and compare bids in one place",
        "All communications in one Shipment Workspace",
      ],
      limit: "3 shipments/month",
      cta: "Start Free",
    },
    {
      id: "paid_monthly",
      name: "Unlimited",
      price: 25,
      fmPrice: 20,
      billing: "monthly",
      badge: null,
      description: "For businesses that ship regularly",
      features: [
        "Post once — your partners are notified automatically",
        "Unlimited shipments",
        "Receive bids from your partners and the public board",
        "Compare bids side by side",
        "All communications in one Shipment Workspace",
      ],
      limit: null,
      cta: "Get Started",
    },
  ],
  carrier: [
    {
      id: "free",
      name: "Free",
      price: null,
      billing: null,
      badge: null,
      description: "Bid on loads from partners who invite you",
      features: [
        "Bid on loads you're invited to",
        "All communications in one Shipment Workspace",
        "Build your reputation",
        "Receive load notifications from shippers",
      ],
      limit: "Invited loads only",
      cta: "Start Free",
    },
    {
      id: "search_and_bid_monthly",
      name: "Search & Bid",
      price: 25,
      fmPrice: null,
      billing: "monthly",
      badge: "Most Popular",
      description: "Find and bid on any load on the board",
      features: [
        "Everything in Free",
        "Search the full public load board",
        "Bid on any available load",
        "Advanced load filters",
      ],
      limit: null,
      cta: "Get Started",
    },
  ],
  broker: [
    {
      id: "free",
      name: "Free",
      price: null,
      billing: null,
      badge: null,
      description: "Bid on loads from partners who invite you",
      features: [
        "Bid on loads you're invited to",
        "All communications in one Shipment Workspace",
        "Build your reputation",
        "Receive load notifications from shippers",
      ],
      limit: "Invited loads only",
      cta: "Start Free",
    },
    {
      id: "search_and_bid_monthly",
      name: "Search & Bid",
      price: 25,
      fmPrice: null,
      billing: "monthly",
      badge: null,
      description: "Find and bid on any load on the board",
      features: [
        "Everything in Free",
        "Search the full public load board",
        "Bid on any available load",
        "Advanced load filters",
      ],
      limit: null,
      cta: "Get Started",
    },
    {
      id: "search_bid_post_monthly",
      name: "Search, Bid & Post",
      price: 45,
      fmPrice: 60,
      billing: "monthly",
      badge: "Full Access",
      description: "The complete broker experience",
      features: [
        "Everything in Search & Bid",
        "Post loads on behalf of your shipper clients",
        "Full marketplace toolset",
        "All communications in one Shipment Workspace",
      ],
      limit: null,
      cta: "Get Started",
    },
  ],
};

export default function SelectMembership() {
  const { role } = useParams<{ role: string }>();
  const [, setLocation] = useLocation();
  const normalizedRole = (role?.toLowerCase() ?? "shipper") as Role;
  const tiers = TIER_CONFIG[normalizedRole] ?? TIER_CONFIG.shipper;

  const [bannerDismissed, setBannerDismissed] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const connected = params.get("connected");
  const partnerName = params.get("partnerName");

  function handleSelectTier(tierId: string, billing: string | null) {
    if (tierId === "free") {
      setLocation(`/signup/${normalizedRole}?billing=free`);
      return;
    }
    let tier = "Paid";
    if (tierId === "search_and_bid_monthly") tier = "SearchAndBid";
    else if (tierId === "search_bid_post_monthly") tier = "Paid";
    else if (tierId === "paid_monthly") tier = "Paid";
    setLocation(`/signup/${normalizedRole}?billing=${billing ?? "monthly"}&tier=${tier}`);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-4xl">
        {!bannerDismissed && connected === "1" && partnerName && (
          <Card className="p-4 mb-6" data-testid="card-referral-partner-banner">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Users className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">You're now connected with {partnerName} as a partner.</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBannerDismissed(true)}
                data-testid="button-referral-banner-dismiss"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        )}

        {!bannerDismissed && connected === "samerole" && (
          <Card className="p-4 mb-6" data-testid="card-referral-samerole-banner">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Users className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Welcome! Since you're both on the same role, no partner connection was needed.</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBannerDismissed(true)}
                data-testid="button-referral-samerole-banner-dismiss"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        )}

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Choose your plan</h1>
          <p className="text-muted-foreground">
            Start free and upgrade anytime. No credit card required for the free plan.
          </p>
        </div>

        <div className={`grid gap-6 ${tiers.length === 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto"}`}>
          {tiers.map((tier) => {
            return (
              <Card
                key={tier.id}
                className={`p-6 flex flex-col relative ${tier.badge === "Most Popular" ? "border-primary border-2" : ""}`}
              >
                {tier.badge && (
                  <Badge
                    className="absolute -top-3 left-1/2 -translate-x-1/2"
                    variant={tier.badge === "Most Popular" ? "default" : "secondary"}
                  >
                    {tier.badge}
                  </Badge>
                )}

                <div className="mb-4">
                  <h2 className="text-xl font-bold mb-1">{tier.name}</h2>
                  <p className="text-sm text-muted-foreground">{tier.description}</p>
                </div>

                <div className="mb-6">
                  {tier.price === null ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">$0</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">${tier.price}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  )}
                  {tier.limit && (
                    <p className="text-xs text-muted-foreground mt-1">{tier.limit}</p>
                  )}
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full"
                  variant={tier.id === "free" ? "outline" : "default"}
                  onClick={() => handleSelectTier(tier.id, tier.billing)}
                  data-testid={`button-select-tier-${tier.id}`}
                >
                  {tier.cta}
                </Button>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          You can upgrade or change your plan at any time from Settings.
        </p>
      </div>
    </div>
  );
}
